import { compact, invert } from 'lodash';

import * as K from 'common/constants';
import { numericStringCompare } from 'common/util/string';
import { arrayToHash } from 'common/util/array';
import { Sources } from 'common/graphqlTypes';
import { SourceAndLogData, SourceAndLogDataSet, UrlParams } from 'common/types';

/**
 * These functions are related to the source and log data we get from the server, which gets exposed through dropdowns in
 * the sidebar. The size of the data is possibly huge, so we do some optimizing by processing it into a form which can
 * be searched quickly.
 */

/**
 * Process the server data so that we can optimize filtering. The data, especially the list of logs, can be large.
 * Since sorting is the biggest bottleneck, we sort the data here so that when it's time to filter we can iterate
 * through a sorted list, guaranteeing sorted results.
 *
 * Each type of data (source, log, cluster, controller) has a list called 'all' with names, and a list called
 * 'sorted' which is an ordered list of indexes from the 'all' list. Logs and controllers have a two-dimensional
 * array called 'containedBy' which is used to see which sources/clusters contain which controller/logs.
 *
 * @param sourceAndLogData    raw source/log data
 *
 * TODO: consider using Map for SourceAndLogData and possibly SourceAndLogDataSet, for both type safety and
 * performance reasons. https://levelup.gitconnected.com/building-type-safe-dictionaries-in-typescript-a072d750cbdf
 */
export const updateSourcesAndLogs = (sourceAndLogData: Sources): SourceAndLogData => {
  const data = {} as SourceAndLogData;

  let type: K.SEARCH_FIELD_DATA_TYPE; // source type
  let subType: K.SEARCH_FIELD_DATA_TYPE; // sub-type (log or controller)

  // create data structure; first get list of data types
  const dataTypes = Object.values(K.SEARCH_FIELD_DATA_TYPE);
  dataTypes.forEach((dataType: K.SEARCH_FIELD_DATA_TYPE) => {
    data[dataType] = {} as SourceAndLogDataSet;
    data[dataType].all = [];
    data[dataType].sorted = [];
    if (dataType === K.SEARCH_FIELD_DATA_TYPE.LOG || dataType === K.SEARCH_FIELD_DATA_TYPE.CONTROLLER) {
      data[dataType].containedBy = {} as Record<string, Array<number>>;
    }
  });

  let indexes;

  // Process sources and logs

  const sources = sourceAndLogData.hosts || [];

  // get a list of all known logs and sort it
  type = K.SEARCH_FIELD_DATA_TYPE.SOURCE;
  subType = K.SEARCH_FIELD_DATA_TYPE.LOG;
  const logHash = {} as Record<string, boolean>;
  sources.forEach((source) => {
    source.logFiles.forEach((log) => {
      logHash[log] = true;
    });
  });
  const allLogs = Object.keys(logHash).sort(numericStringCompare); // main bottleneck is this sort call
  data[subType].all = allLogs;
  data[subType].sorted = Array.from(allLogs.keys());

  // this back-map will help us convert a source's list of logs to indexes
  const logsByName = invert(allLogs);

  sources.forEach((source, s) => {
    const hostName = source.name;
    data[type].all.push(hostName);
    const logs = source.logFiles.map((logFile) => logsByName[logFile]);
    logs.forEach((log) => {
      // each log/controller has a list of sources/clusters that contain it
      const contained = data[subType] as SourceAndLogDataSet;
      if (contained && contained.containedBy) {
        if (!contained.containedBy[log]) {
          contained.containedBy[log] = [];
        }
        contained.containedBy[log].push(s);
      }
    });
  });

  const allSources: Array<string> = data[type].all;
  indexes = Array.from(allSources.keys());
  data[type].sorted = indexes.sort((a, b) => numericStringCompare(allSources[a], allSources[b]));

  // Process kubernetes clusters and their controllers
  type = K.SEARCH_FIELD_DATA_TYPE.CLUSTER;
  subType = K.SEARCH_FIELD_DATA_TYPE.CONTROLLER;
  const clusters = sourceAndLogData.kubeClusters || [];
  // This is a map of controller names to a map of the parent clusters associated with the controller name to the
  // kind associated with that cluster-controller combination. This allows us to look up controller kind by the
  // uniquely identifying combination of controller name and cluster name.
  const controllerHash: Record<string, Record<string, string>> = {};
  clusters.forEach((cluster) => {
    data[type].all.push(cluster.name);
    cluster.controllers.forEach((controller) => {
      // Add this cluster-kind key-value pair to the map for this controller name.
      if (controllerHash[controller.name]) {
        controllerHash[controller.name][cluster.name] = controller.kind;
      } else {
        controllerHash[controller.name] = { [cluster.name]: controller.kind };
      }
    });
  });

  data[subType].all = Object.keys(controllerHash);
  data[subType].controllerNameToClusterNameToKind = controllerHash;
  const allControllers = data[subType].all;
  indexes = Array.from(allControllers.keys());
  data[subType].sorted = indexes.sort((a, b) => numericStringCompare(allControllers[a], allControllers[b]));
  const controllerToClusterMap: Record<string, number> = {};
  allControllers.forEach((controller, index) => {
    controllerToClusterMap[controller] = index;
  });
  clusters.forEach((cluster, c) => {
    cluster.controllers.forEach((controller) => {
      const controllerNum = controllerToClusterMap[controller.name];
      const contained = data[subType] as SourceAndLogDataSet;
      if (contained && contained.containedBy) {
        if (!contained.containedBy[controllerNum]) {
          contained.containedBy[controllerNum] = [];
        }
        contained.containedBy[controllerNum].push(c);
      }
    });
  });
  const allClusters: Array<string> = data[type].all;
  indexes = Array.from(allClusters.keys());
  data[type].sorted = indexes.sort((a, b) => numericStringCompare(allClusters[a], allClusters[b]));
  data[type].nameToIndex = invert(data[type].all);

  return data;
};

// convert list of indexes into list of names
const getNames = (data: SourceAndLogData, list: Array<number>, type: keyof SourceAndLogData): Array<string> => {
  if (data[type] && data[type].all) {
    return list.map((item) => data[type].all[item]);
  }
  return [];
};

// return a list of the controllers/logfiles contained by the given sources
const getLogsFromSources = (
  data: SourceAndLogData,
  logType: keyof SourceAndLogData,
  sources: Array<string>,
): Array<string> => {
  if (!sources || sources.length === 0) {
    return getNames(data, data[logType] ? data[logType].sorted : [], logType);
  }

  const sourceHash = arrayToHash(sources);
  const sourceType =
    logType === K.SEARCH_FIELD_DATA_TYPE.CONTROLLER
      ? K.SEARCH_FIELD_DATA_TYPE.CLUSTER
      : K.SEARCH_FIELD_DATA_TYPE.SOURCE;
  let logs: Array<number> = [];
  if (data[logType]) {
    const logData = data[logType] as SourceAndLogDataSet;
    logs = logData.sorted.filter((log) => {
      if (logData.containedBy) {
        const cb = logData.containedBy[log];
        for (let i = 0; i < cb.length; i += 1) {
          if (sourceHash[data[sourceType].all[cb[i]]]) {
            return true;
          }
        }
      }
      return false;
    });
  }

  return getNames(data, logs, logType);
};

/**
 * Get a list of all the sources from the data. It will include Kubernetes clusters followed by server hosts.
 *
 * @param data    processed source/log data
 */
export const getSources = (data: SourceAndLogData): Array<string> => {
  let clusters: Array<string> = [];
  if (data[K.SEARCH_FIELD_DATA_TYPE.CLUSTER]) {
    clusters = getNames(data, data[K.SEARCH_FIELD_DATA_TYPE.CLUSTER].sorted, K.SEARCH_FIELD_DATA_TYPE.CLUSTER);
  }
  let sources: Array<string> = [];
  if (data[K.SEARCH_FIELD_DATA_TYPE.SOURCE]) {
    sources = getNames(data, data[K.SEARCH_FIELD_DATA_TYPE.SOURCE].sorted, K.SEARCH_FIELD_DATA_TYPE.SOURCE);
  }

  return compact(clusters.concat(sources));
};

/**
 * Get a list of all the logs from the data. It will include Kubernetes controllers followed by logfiles.
 *
 * @param data    processed source/log data
 * @param sources list of sources
 */
export const getLogs = (data: SourceAndLogData, sources: Array<string> = []): Array<string> => {
  const controllers = getLogsFromSources(data, K.SEARCH_FIELD_DATA_TYPE.CONTROLLER, sources);
  const logs = getLogsFromSources(data, K.SEARCH_FIELD_DATA_TYPE.LOG, sources);

  return compact(controllers.concat(logs));
};

/**
 * Given a set of params, return the first non-empty source or log value.
 *
 * @param params      a set of URL params
 * @param isSource    if true, return source value; if false, return log/controller value
 *
 * @returns source or log value, or empty string
 */
export const getValueFromParams = (params: UrlParams, isSource: boolean): string => {
  // first dropdown has host or k8s cluster
  if (isSource) {
    return params[K.URL_PARAM.LOG_SOURCE] || params[K.URL_PARAM.KUBERNETES_CLUSTER] || '';
  }

  // second dropdown can have logfile or any of the k8s controller types
  const logTerms = K.K8S_CONTROLLERS.map((term) => K.FACET_TERM_TO_QUERY_TERM[term]) as unknown as Array<K.URL_PARAM>;
  logTerms.unshift(K.URL_PARAM.LOG);
  const logTerm = logTerms.find((term) => !!params[term]);
  return logTerm ? params[logTerm] || '' : '';
};

/**
 * Given a value and its type (source or log), set URL params. If the value is empty, we clear all the associated params.
 * If we find the value in our source/log data, then we know exactly which param to set. Otherwise we'll set the most
 * generic params, which are 'logSource' and 'log'.
 *
 * @param sourcesAndLogs    hash of source, log, cluster, and controller data
 * @param value             value to set
 * @param isSource          if true, set source param(s); else set log param(s)
 * @param curSource         current source or cluster value, used to disambiguate controller kinds
 * @param curLog            current log or controller value
 *
 * @returns a set of URL params with appropriate values set
 */
export const getParamsFromValue = (
  sourcesAndLogs: SourceAndLogData | null,
  value: string,
  isSource: boolean,
  curSource = '',
  curLog = '',
): UrlParams => {
  const urlParams = {} as UrlParams;

  // clear controller terms within the given params
  const clearControllerTerms = (params: UrlParams): void => {
    K.K8S_CONTROLLERS.forEach((controllerFacetTerm) => {
      const controllerQueryTerm = K.FACET_TERM_TO_QUERY_TERM[controllerFacetTerm];
      params[controllerQueryTerm as unknown as K.URL_PARAM] = '';
    });
  };

  // if the input has been cleared, clear the related params
  if (!value) {
    if (isSource) {
      urlParams[K.URL_PARAM.LOG_SOURCE] = '';
      urlParams[K.URL_PARAM.KUBERNETES_CLUSTER] = '';
    } else {
      urlParams[K.URL_PARAM.LOG] = '';
      clearControllerTerms(urlParams);
    }
    return urlParams;
  }

  // return the kind for a controller given the current cluster
  const getKindForController = (controllerName: string, source: string): string => {
    const clusterToKindMap = sourcesAndLogs?.controller?.controllerNameToClusterNameToKind?.[controllerName] || {};
    return clusterToKindMap?.[source] || Object.values(clusterToKindMap)[0] || '';
  };

  // set the appropriate param for a controller based on its kind
  const setController = (params: UrlParams, controllerName: string, kind: string): void => {
    const queryTerm = K.FACET_TERM_TO_QUERY_TERM[kind as K.FACET_TERM];
    if (queryTerm) {
      urlParams[queryTerm as unknown as K.URL_PARAM] = controllerName;
    }
    urlParams[K.URL_PARAM.LOG] = '';
  };

  // figure out what type of value it is based on presence in our source/log data; clear the converse type
  const isCluster = isSource && !!sourcesAndLogs?.cluster?.nameToIndex?.[value];
  const isController = !isSource && sourcesAndLogs?.controller?.all.includes(value);
  // If the current selection in the log dropdown is a controller, then we need to determine the appropriate
  // controller kind. If a cluster is also selected in the source dropdown, then we want to use the kind
  // property associated with the controller that is a child of the selected cluster. Otherwise, we just take
  // the value of the first kind we encounter in the map of clusters to kinds.
  if (isCluster) {
    urlParams[K.URL_PARAM.KUBERNETES_CLUSTER] = value;
    urlParams[K.URL_PARAM.LOG_SOURCE] = '';
    const kind = getKindForController(curLog, value) as K.FACET_TERM;
    if (kind) {
      clearControllerTerms(urlParams);
      setController(urlParams, curLog, kind);
    }
  } else if (isSource) {
    urlParams[K.URL_PARAM.LOG_SOURCE] = value;
    urlParams[K.URL_PARAM.KUBERNETES_CLUSTER] = '';
  } else if (isController) {
    const kind = getKindForController(value, curSource);
    setController(urlParams, value, kind);
  } else if (!isSource) {
    urlParams[K.URL_PARAM.LOG] = value;
    K.K8S_CONTROLLERS.forEach((facetTerm) => {
      const queryTerm = K.FACET_TERM_TO_QUERY_TERM[facetTerm];
      urlParams[queryTerm as unknown as K.URL_PARAM] = '';
    });
  }

  return urlParams;
};
